import UtilService from "../../../../services/UtilService";
import Logger from "../../../../services/Logger";
import { isWordPuzzleOpenAccess } from "../puzzleTierAccess";

/**
 * Helper functions for puzzle card components
 */

/**
 * Get progress for grid puzzles (Word Search / Hidden Message)
 */
export const getGridProgress = (puzzle, isInProgress) => {
    if (!puzzle.hwPlay || puzzle.hwPlay.isCompleted || puzzle.hwPlay.completed) {
        return null;
    }

    // Handle wordsFound as either a string (split by \n) or a number
    let wordsFoundCount = 0;
    if (typeof puzzle.hwPlay.wordsFound === 'string') {
        wordsFoundCount = puzzle.hwPlay.wordsFound.split('\n').filter(word => word.trim().length > 0).length;
    } else if (typeof puzzle.hwPlay.wordsFound === 'number') {
        wordsFoundCount = puzzle.hwPlay.wordsFound;
    }

    let wordsTotal = puzzle.hwPlay.wordsTotal;

    // For HIDDEN_MESSAGE type, wordsTotal should be -1 (don't show progress)
    if (puzzle.type === 'HIDDEN_MESSAGE' && wordsTotal === -1) {
        return null;
    }

    // If wordsTotal is 0 or invalid, don't show progress
    if (!wordsTotal || wordsTotal <= 0) {
        return null;
    }

    const progress = Math.min(100, Math.round((wordsFoundCount / wordsTotal) * 100));
    return progress;
};

/**
 * Get progress for crossword puzzles
 */
export const getCrossProgress = (puzzle, isInProgress) => {
    // Debug: Log puzzle data structure for in-progress puzzles
    if (isInProgress && !puzzle.crossPlay) {
        Logger.log('Crossword puzzle missing crossPlay:', {
            id: puzzle.id,
            title: puzzle.title,
            type: puzzle.type,
            hasCrossPlay: !!puzzle.crossPlay,
            puzzleKeys: Object.keys(puzzle)
        });
    }

    if (!puzzle.crossPlay || puzzle.crossPlay.isCompleted || puzzle.crossPlay.completed) {
        return null;
    }

    // Handle wordsFound as either a string (split by \n) or a number
    let wordsFoundCount = 0;
    if (typeof puzzle.crossPlay.wordsFound === 'string') {
        wordsFoundCount = puzzle.crossPlay.wordsFound.split('\n').filter(word => word.trim().length > 0).length;
    } else if (typeof puzzle.crossPlay.wordsFound === 'number') {
        wordsFoundCount = puzzle.crossPlay.wordsFound;
    } else if (Array.isArray(puzzle.crossPlay.wordsFound)) {
        wordsFoundCount = puzzle.crossPlay.wordsFound.length;
    }

    let wordsTotal = puzzle.crossPlay.wordsTotal;

    // If wordsTotal is not available, try to calculate from words array
    if (!wordsTotal || wordsTotal <= 0) {
        if (puzzle.words) {
            // If words is a string, split by newline
            if (typeof puzzle.words === 'string') {
                wordsTotal = puzzle.words.split('\n').filter(word => word.trim().length > 0).length;
            } else if (Array.isArray(puzzle.words)) {
                wordsTotal = puzzle.words.length;
            }
        }
    }

    // If wordsTotal is still 0 or invalid, don't show progress
    if (!wordsTotal || wordsTotal <= 0) {
        return null;
    }

    const progress = Math.min(100, Math.round((wordsFoundCount / wordsTotal) * 100));
    return progress;
};

/**
 * Get play data for grid puzzles
 */
export const getGridPlayData = (puzzle) => {
    return puzzle.hwPlay || null;
};

/**
 * Get play data for crossword puzzles
 */
export const getCrossPlayData = (puzzle) => {
    return puzzle.crossPlay || null;
};

/** API may set isCompleted without finishedAt — matches PuzzleCardBase / browse captions. */
export const isPlayDataComplete = (pd) => {
    if (!pd) return false;
    if (pd.finishedAt) return true;
    if (pd.isCompleted === true) return true;
    if (pd.completed === true) return true;
    return false;
};

/**
 * Get word count for grid puzzles
 */
export const getGridWordCount = (puzzle) => {
    const count = puzzle.words?.split('\n').length || 0;
    // For HIDDEN_MESSAGE, subtract 1 (hidden message word)
    return puzzle.type === 'HIDDEN_MESSAGE' ? count - 1 : count;
};

/**
 * Get word count for crossword puzzles
 */
export const getCrossWordCount = (puzzle) => {
    return puzzle.words?.split('\n').length || 0;
};

/**
 * Get navigation path for grid puzzles
 */
export const getGridNavigationPath = (puzzle) => {
    if (puzzle.type === 'WORD_SEARCH') {
        return `/puzzle/play-word-search/${puzzle.id}`;
    } else if (puzzle.type === 'HIDDEN_MESSAGE') {
        return `/puzzle/play-hidden-message/${puzzle.id}`;
    }
    return null;
};

/**
 * Get navigation path for crossword puzzles
 */
export const getCrossNavigationPath = (puzzle) => {
    if (puzzle.type === 'CROSSWORD') {
        return `/puzzle/play-cross/${puzzle.id}`;
    }
    return null;
};

/** Sliding tile puzzles — story group id in URL */
export const getSlidingNavigationPath = (puzzle) => {
    if (puzzle?.type === "SLIDING" && puzzle?.id) {
        return `/puzzle/play-sliding/${encodeURIComponent(puzzle.id)}`;
    }
    return null;
};

export const getSlidingProgress = () => null;

export const getSlidingPlayData = (puzzle) => puzzle?.slidingPlay ?? null;

export const getSlidingWordCount = () => 0;

export const getSlidingCardContentPadding = (userPayPlan) => {
    return !UtilService.isStandardPayPlan(userPayPlan) ? 6 : 2;
};

/**
 * Get CardContent padding for grid puzzles
 */
export const getGridCardContentPadding = (userPayPlan, isNewPuzzle) => {
    return !UtilService.isStandardPayPlan(userPayPlan) ? 6 : 2;
};

/**
 * Get CardContent padding for crossword puzzles
 * Same as grid: Standard subscribers get 2, others get 6. NEW icon is positioned above the card, so no extra padding needed.
 */
export const getCrossCardContentPadding = (userPayPlan) => {
    return !UtilService.isStandardPayPlan(userPayPlan) ? 6 : 2;
};

function firstNonEmptyTagArray(...candidates) {
    for (const a of candidates) {
        if (Array.isArray(a) && a.some((t) => t != null && String(t).trim() !== "")) {
            return a;
        }
    }
    return null;
}

/**
 * Raw tag strings for puzzle cards from template/tpl/root lists only (`topic.tags` is intentionally ignored).
 */
export function resolvePuzzleCardRawTags(puzzle) {
    if (!puzzle || typeof puzzle !== "object") return [];
    const merged =
        firstNonEmptyTagArray(
            puzzle.template?.tags,
            puzzle.crossword?.crossTpl?.tags,
            puzzle.crossTpl?.tags,
            puzzle.grid?.gridTpl?.tags,
            puzzle.gridTpl?.tags,
            puzzle.tags,
        ) ?? [];
    return Array.isArray(merged) ? merged : [];
}

/**
 * List pages: Easy / open-access puzzles before credit-gated ones on each loaded page.
 * Word puzzles use difficulty; other types still use `free === true`.
 */
export const sortPuzzlesFreeFirst = (puzzles) => {
    if (!puzzles?.length) {
        return puzzles || [];
    }
    const openRank = (p) => {
        const type = String(p?.type ?? "").toUpperCase();
        if (type === "CROSSWORD" || type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE") {
            return isWordPuzzleOpenAccess(p?.difficultyLevel) ? 0 : 1;
        }
        return p?.free === true ? 0 : 1;
    };
    return [...puzzles].sort((a, b) => openRank(a) - openRank(b));
};

